import React, { useState, useEffect } from "react";
import {
  Download,
  FileText,
  FileSpreadsheet,
  IndianRupee,
  Ticket,
} from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { format } from "date-fns";
import {
  server_post_data,
  get_funzone_reports,
} from "../ServiceConnection/serviceconnection.js";

// FIX 5: Outside component — recreate nahi hogi har render pe
const formatTime = (time) => {
  if (!time) return "-";
  const date = new Date(time);
  if (isNaN(date.getTime())) return "-";
  return format(date, "dd MMM, HH:mm");
};

export default function ReportsPage() {
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState({
    total_revenue: 0,
    total_tickets: 0,
  });
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(false);

  /* ---------------- FETCH ---------------- */

  // FIX 1: fetchReports plain async function — useCallback hata diya
  // FIX 2: Only fetch when BOTH dates provided OR both empty (all data)
  const fetchReports = async (from, to) => {
    setLoading(true);
    try {
      const fd = new FormData();
      if (from) fd.append("from_date", from);
      if (to) fd.append("to_date", to);

      const response = await server_post_data(get_funzone_reports, fd);

      if (response.data.status === 1) {
        setReports(response.data.data || []);
        setSummary({
          total_revenue: response.data.summary?.total_revenue || 0,
          total_tickets: response.data.summary?.total_tickets || 0,
        });
      } else {
        setReports([]);
        setSummary({ total_revenue: 0, total_tickets: 0 });
      }
    } catch (error) {
      console.error("Report API error", error);
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load — all data
  useEffect(() => {
    fetchReports("", "");
  }, []);

  // FIX 2: Only call API when both dates are selected
  useEffect(() => {
    if (dateFrom && dateTo) {
      fetchReports(dateFrom, dateTo);
    }
  }, [dateFrom, dateTo]);

  /* ---------------- CLEAR ---------------- */

  const clearFilters = () => {
    setDateFrom("");
    setDateTo("");
    fetchReports("", ""); // FIX 2: explicitly fetch all data on clear
  };

  /* ---------------- EXPORT HELPERS ---------------- */

  // FIX 3: formatted time in exports bhi
  const exportRows = reports.map((t, i) => ({
    sno: i + 1,
    date: t.date,
    parent: t.parent_name || "-",
    kids_count: t.no_of_kids || "-",
    kid: t.kid_name || "-",
    mobile: t.mobile || "-",
    entry: formatTime(t.entry_time),
    exit: formatTime(t.exit_time),
    duration: t.duration || "-",
    sub_total: t.sub_total || 0,
    gst_percentage: t.gst_percentage || 0,
    cgst_amount: t.cgst_amount || 0,
    sgst_amount: t.sgst_amount || 0,
    gst_amount: t.gst_amount || 0,
    amount: t.amount,
    payment: t.payment_mode,
    cash_amount: t.cash_amount || 0,
    upi_amount: t.upi_amount || 0,
  }));

  /* CSV */
  const exportCSV = () => {
    // FIX 4: Guard — empty data pe export nahi
    if (!reports.length) return;

    const header =
      "S.No,Date,Parent Name,No. of Kids,Kid Name,Mobile,Entry,Exit,Duration,Sub Total,GST %,CGST Amount,SGST Amount,Total Amount,Payment,Cash Amount,UPI Amount\n";
    const rows = exportRows
      .map(
        (r) =>
          `${r.sno},${r.date},${r.parent},${r.kids_count},${r.kid},${r.mobile},${r.entry},${r.exit},${r.duration},${r.sub_total},${r.gst_percentage},${r.cgst_amount},${r.sgst_amount},${r.amount},${r.payment},${r.cash_amount},${r.upi_amount}`
      )
      .join("\n");

    const blob = new Blob([header + rows], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "funzone-report.csv";
    link.click();
  };

  /* PDF */
  const exportPDF = () => {
    if (!reports.length) return; // FIX 4

    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text("Fun City Playzone - Report", 14, 20);

    autoTable(doc, {
      startY: 30,
      head: [
        [
          "S.No",
          "Date",
          "Parent",
          "No. of Kids",
          "Kid Name",
          "Mobile",
          "Entry",
          "Exit",
          "Duration",
          "Sub Total",
          "GST %",
          "CGST",
          "SGST",
          "Total Amount",
          "Payment Mode",
          "Cash",
          "UPI"
        ],
      ],
      body: exportRows.map((r) => [
        r.sno,
        r.date,
        r.parent,
        r.kids_count,
        r.kid,
        r.mobile,
        r.entry,
        r.exit,
        r.duration,
        `₹${r.sub_total}`,
        `${r.gst_percentage}%`,
        // `₹${r.gst_amount}`,
        `₹${r.cgst_amount}`,
        `₹${r.sgst_amount}`,
        `₹${r.amount}`,
        r.payment,
        `₹${r.cash_amount}`,
        `₹${r.upi_amount}`
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [139, 92, 246] },
    });

    doc.save("funzone-report.pdf");
  };

  /* EXCEL */
  const exportExcel = () => {
    if (!reports.length) return; // FIX 4

    const data = exportRows.map((r) => ({
      "S.No": r.sno,
      Date: r.date,
      "Parent Name": r.parent,
      "No. of Kids": r.kids_count,
      "Kid Name": r.kid,
      Mobile: r.mobile,
      Entry: r.entry,
      Exit: r.exit,
      Duration: r.duration,
      "Sub Total": r.sub_total,
      "GST %": r.gst_percentage,
      "CGST Amount": r.cgst_amount,
      "SGST Amount": r.sgst_amount,
      // "GST Amount": r.gst_amount,
      "Total Amount": r.amount,
      Payment: r.payment,

      "Cash Amount": r.cash_amount,
      "UPI Amount": r.upi_amount,
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, "funzone-report.xlsx");
  };

  /* ---------------- UI ---------------- */

  return (
    <div className="pl-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
        <p className="text-gray-500">View and export billing data</p>
      </div>

      {/* FILTER + CARDS */}
      <div className="mb-6 rounded-xl border bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-end justify-between gap-4">
          {/* DATE FILTER */}
          <div className="flex items-end gap-4">
            <div className="flex flex-col">
              <label className="text-xs text-gray-500 mb-1">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="border rounded-lg px-3 py-1.5 text-sm w-36"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-xs text-gray-500 mb-1">To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="border rounded-lg px-3 py-1.5 text-sm w-36"
              />
            </div>

            <button
              onClick={clearFilters}
              className="border rounded-md px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 h-[32px]"
            >
              Clear
            </button>
          </div>

          {/* SUMMARY CARDS */}
          <div className="flex gap-3">
            <div className="flex items-center gap-3 bg-white border rounded-xl px-5 py-3 shadow-sm">
              <div className="bg-green-100 p-2 rounded-lg">
                <IndianRupee size={18} className="text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Total Revenue</p>
                <p className="text-lg font-bold">₹{summary.total_revenue}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-white border rounded-xl px-5 py-3 shadow-sm">
              <div className="bg-purple-100 p-2 rounded-lg">
                <Ticket size={18} className="text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Tickets</p>
                <p className="text-lg font-bold">{summary.total_tickets}</p>
              </div>
            </div>
          </div>
        </div>

        {/* EXPORT BUTTONS — FIX 4: disabled when no data */}
        <div className="flex flex-wrap gap-2 mt-4">
          <button
            onClick={exportCSV}
            disabled={!reports.length}
            className="flex items-center gap-1 border rounded-md px-3 py-1.5 text-xs hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download size={14} /> CSV
          </button>

          <button
            onClick={exportPDF}
            disabled={!reports.length}
            className="flex items-center gap-1 bg-pink-500 text-white rounded-md px-3 py-1.5 text-xs hover:bg-pink-600 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FileText size={14} /> PDF
          </button>

          <button
            onClick={exportExcel}
            disabled={!reports.length}
            className="flex items-center gap-1 bg-green-500 text-white rounded-md px-3 py-1.5 text-xs hover:bg-green-600 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FileSpreadsheet size={14} /> Excel
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                {[
                  "S.No",
                  "Date",
                  "Parent Name",
                  "No. of Kids",
                  "Kid Name",
                  "Mobile",
                  "Entry",
                  "Exit",
                  "Duration",
                  "Sub Total",
                  "GST %",
                  "CGST",
                  "SGST",
                  "Total Amount",
                  "Payment",
                  "Cash",
                  "UPI",
                ].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan="17" className="text-center py-6 text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : reports.length === 0 ? (
                <tr>
                  <td colSpan="17" className="text-center py-6 text-gray-400">
                    No data found
                  </td>
                </tr>
              ) : (
                reports.map((t, i) => (
                  <tr key={t.billing_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">{i + 1}</td>
                    <td className="px-4 py-3">{t.date}</td>
                    <td className="px-4 py-3">{t.parent_name || "-"}</td>
                    <td className="px-4 py-3">{t.no_of_kids || "-"}</td>
                    <td className="px-4 py-3 font-medium">{t.kid_name || "-"}</td>
                    <td className="px-4 py-3">{t.mobile || "-"}</td>
                    <td className="px-4 py-3">{formatTime(t.entry_time)}</td>
                    <td className="px-4 py-3">{formatTime(t.exit_time)}</td>
                    <td className="px-4 py-3">{t.duration}</td>

                    <td className="px-4 py-3">₹{Number(t.sub_total || 0).toFixed(2)}</td>
                    <td className="px-4 py-3">{t.gst_percentage || 0}%</td>
                    <td className="px-4 py-3">₹{Number(t.cgst_amount || 0).toFixed(2)}</td>
                    <td className="px-4 py-3">₹{Number(t.sgst_amount || 0).toFixed(2)}</td>

                    {/* <td className="px-4 py-3">₹{Number(t.gst_amount || 0).toFixed(2)}</td> */}
                    <td className="px-4 py-3 font-semibold">₹{Number(t.amount || 0).toFixed(2)}</td>

                    <td className="px-4 py-3">
                      {t.payment_mode?.toLowerCase() === "upi" && (
                        <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full">
                          UPI
                        </span>
                      )}
                      {t.payment_mode?.toLowerCase() === "cash" && (
                        <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">
                          Cash
                        </span>
                      )}
                      {t.payment_mode?.toLowerCase() === "split" && (
                        <span className="bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full">
                          Split
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">₹{Number(t.cash_amount || 0).toFixed(2)}</td>
                    <td className="px-4 py-3">₹{Number(t.upi_amount || 0).toFixed(2)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
